import { useState } from 'react'

// Per-entry truncation for parent-facing free text.
//
// Contract: docs/contracts/FEATURE_CONTRACT_PORTAL_CLASS_DISPLAY_2026_08.md
// § "Feedback truncation". The newest feedback format restates the session's
// learning objectives verbatim (771/581/478 chars measured live), so three
// entries alone filled 39% of a 420x900 phone page.
//
// The limit is defined in Unicode CODE POINTS, not UTF-16 units: a naive
// `slice(0, 200)` cuts an astral character (emoji) in half and emits a lone
// surrogate. `Array.from` iterates code points, so a slice of that array can
// never split a surrogate pair.
export const TRUNCATION_LIMIT = 200
const ELLIPSIS = '…'

// Latin word characters only. CJK deliberately does NOT count as a word
// character: Chinese prose carries no spaces, so a word-boundary back-off
// would have nothing to find and would either hard-cut anyway or (worse, if
// CJK counted) walk the whole string looking for a space that never comes.
// Every CJK character is a legal break point.
const WORD_CHAR = /[0-9A-Za-zÀ-ɏ'’-]/

function _isSpace(ch) {
  return typeof ch === 'string' && /\s/.test(ch)
}

/**
 * Truncate at a word/CJK boundary.
 *
 * @returns {{truncated: boolean, visible: string}} `visible` already carries
 *   the ellipsis when truncated; when not truncated it is the source string.
 */
export function truncateToLimit(text, limit = TRUNCATION_LIMIT) {
  const source = typeof text === 'string' ? text : ''
  const codePoints = Array.from(source)
  if (codePoints.length <= limit) {
    return { truncated: false, visible: source }
  }

  let cut = limit
  const last = codePoints[limit - 1]
  const next = codePoints[limit]
  // Only back off when the cut would land inside a Latin word. A boundary that
  // already sits on whitespace, punctuation, CJK, or an emoji is kept as-is.
  // The back-off is FLOORED at 70% of the limit: a short lead-in followed by
  // one long unbroken token (a URL, a hyphen-joined run) must not collapse
  // the visible text to a couple of characters — that is the contract's
  // "truncation lies" failure mode. Past the floor, hard-cut mid-word.
  if (!_isSpace(last) && !_isSpace(next) && WORD_CHAR.test(last) && WORD_CHAR.test(next)) {
    const floor = Math.floor(limit * 0.7)
    for (let i = limit - 1; i > floor; i -= 1) {
      if (_isSpace(codePoints[i])) {
        cut = i
        break
      }
    }
  }

  const visible = codePoints.slice(0, cut).join('').replace(/\s+$/, '')
  return { truncated: true, visible: `${visible}${ELLIPSIS}` }
}

/**
 * Renders `text` capped at `limit` code points with a show more / show less
 * toggle. Text at or under the limit renders with no toggle at all — an
 * expander that expands nothing is a lie about there being more to read.
 *
 * Expand state is per instance, so one long entry in a list does not expand
 * its siblings.
 */
export default function TruncatedText({
  text,
  t,
  limit = TRUNCATION_LIMIT,
  className,
  style,
  quoted = false,
}) {
  const [expanded, setExpanded] = useState(false)
  // Trim first, or trailing whitespace past the limit buys a "show more" that
  // reveals nothing but a blank line — the contract's "truncation lies"
  // failure mode. `.feedback-text` is `white-space: pre-wrap`, so a trailing
  // newline from an LLM-written entry is both real and visible.
  const source = text == null ? '' : String(text).trim()
  const { truncated, visible } = truncateToLimit(source, limit)
  const body = truncated && !expanded ? visible : source
  const label = (key) => (typeof t === 'function' ? t(key) : key)

  return (
    <p className={className} style={style}>
      {quoted ? `"${body}"` : body}
      {truncated && (
        <button
          type="button"
          className="truncate-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? label('showLess') : label('showMore')}
        </button>
      )}
    </p>
  )
}
